// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose, Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Table,
  Spinner,
  Warning,
  Button,
  SvgIcon,
  PageNavigationButtons,
  SpanWithData,
  Modal,
  ClientDivision,
  ClientPayrollCycle,
  NavLink
} from '../../'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor,
  readablePhoneNumber
} from '../../../utils'


class BulkMessageAudienceMembers extends PureComponent {
  static propTypes = {
    audienceId: PropTypes.string.isRequired,
    employmentType: PropTypes.string,
    employmentStatus: PropTypes.string,
    createdAtStartDate: PropTypes.string,
    createdAtEndDate: PropTypes.string,
    search: PropTypes.string,
    recordsPerPage: PropTypes.number,
    selectableRows: PropTypes.bool
  }

  static defaultProps = {
    recordsPerPage: 10,
    selectableRows: false
  }

  state = {
    recordsPerPage: this.props.recordsPerPage,
    pageNav: new PageNavigationControl()
  }

  handleSelect = (identityId) => {
    // if (this.state.selectedRow === identityId) {
    //   this.setState({ selectedRow: null })
    // } else {
    //   this.setState(({ selectedRow: identityId }))
    // }
    this.props.history.push(`/employer/${this.props.match.params.employerId}/employees/${identityId}`)
  }

  setResultPerPage = (e) => {
    const numberOfResultsToShow = e.target.value
    this.setState(
      { recordsPerPage: numberOfResultsToShow },
      () => this.props.refetch({ recordsPerPage: numberOfResultsToShow })
    )
  }

  toggleSelectModal = () => (
    this.setState(prevState => ({ showSelectModal: !prevState.showSelectModal }))
  )


  getTableRows = (identities) => {
    if (identities.length > 0) {
      return identities.map(({ node: identity }) => (
        <tr id={identity.id}>
          <td>
            <NavLink to={`/employer/${this.props.match.params.employerId}/employees/${identity.id}`}>
              {identity.fullName}
            </NavLink>
          </td>
          <td>
            {identity.identityValue}
          </td>
          <td>
            {identity.msisdnDefault
              ? readablePhoneNumber(identity.msisdnDefault)
              : '-'
            }
          </td>
          <td>
            <ClientDivision clientId={identity.client.id} />
          </td>
          <td>
            <ClientPayrollCycle clientId={identity.client.id} />
          </td>
          <td>
            {identity.client.employeeNumber}
          </td>
          <td>
            <SpanWithData forField='ClientEmploymentType' searchKey={identity.client.employmentType}  />
          </td>
          <td>
            <SpanWithData forField='ClientStatus' searchKey={identity.client.status} />
          </td>
        </tr>
      ))
    }
    else {
      return [
        <tr>
          <td colSpan={8}>
            <SvgIcon
              variant='utility'
              symbol='info'
              colour='light'
              size='small'
              className='slds-m-right_small'
            />
            No employee records to display
          </td>
        </tr>
      ]
    }
  }

  render() {
    if (this.props.loading) {
      return (
        <Spinner type='tail' className='slds-is-relative' />
      )
    }

    if (this.props.error) {
      return (
        <Warning className='slds-m-around_medium'>
          Error fetching audience member data.
          <Button type='reset' onClick={() => this.props.refetch()} className='slds-m-left_x-small'>
            Retry?
          </Button>
        </Warning>
      )
    }

    const pageNav = this.state.pageNav
    const startCursor = this.props.identities.pageInfo.startCursor
    const totalCount = this.props.identities.totalCount

    const identities = this.props.identities.edges

    return (
      <div className={`${this.state.showSelectModal && 'inactive'} ${this.props.className}`}>
        <Table
          columns={['Name', 'ID / Passport #', 'Cellphone', 'Division', 'Payroll cycle', 'Employee number', 'Employment type', 'Employment status']}
          rows={this.getTableRows(identities)}
          objectRows={true}
          fixedHeader={true}
          // orderBy={this.props.orderBy}
          // changeSort={this.props.changeSort}
          // querySortableColumns={{
          //   'Created at': {
          //     orderByName: 'created_at'
          //   }
          // }}
          selectableRows={identities.length > 0}
          selectedRows={this.props.selectedRows}
          onGlobalSelect={() => {
            const selectActionPerformed = this.props.onGlobalSelect(identities)
            if (selectActionPerformed) {
              // click on global checkbox will simply deselect all if selectedRows equal to recordsPerPage instead
              this.toggleSelectModal()
            }
          }}
          onRowSelect={this.props.onRowSelect}
          className='slds-scrollable_x'
        />
        {identities.length > 0 &&
          <PageNavigationButtons
            onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
            onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
            previousDisabled={pageNav.firstPage}
            nextDisabled={pageNav.lastPage}
            startCursor={startCursor}
            totalCount={totalCount}
            resultsPerPage={this.state.recordsPerPage}
            resultsText='member(s)'
            onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.state.recordsPerPage) })}
            onResultsPerPageChange={this.setResultPerPage}
            className="slds-p-around_medium"
          />
        }
        <Query
          query={gql`
            query($audienceId: UUID!, $divisionId: UUID, $employmentType: String, $employmentStatus: String, $search: String, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime) {
              identities(bulkmessageaudiences_Id: $audienceId, client_Division_Id: $divisionId, client_EmploymentType: $employmentType, client_Status: $employmentStatus, search: $search, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          `}
          variables={{
            search: this.props.search,
            audienceId: decodeBase64(this.props.match.params.audienceId),
            divisionId: this.props.division
              ? decodeBase64(this.props.division)
              : undefined,
            employmentType: this.props.employmentType,
            employmentStatus: this.props.employmentStatus,
            createdAtStartDate: this.props.createdAtStartDate,
            createdAtEndDate: this.props.createdAtEndDate
          }}>
          {({ loading, error, data }) => {
            if (this.state.showSelectModal) {
              return (
                <Modal
                  body={
                    <div>
                      <div className='u-font-size-large slds-m-bottom_large slds-align_absolute-center'>
                        <SvgIcon
                          variant='standard'
                          symbol='question_feed'
                          size='large'
                          colour='light'
                          className='slds-p-right_x-small'
                        />
                        {`Select records on this page only or all ${this.props.identities.totalCount}?`}
                      </div>
                      <div className='slds-align_absolute-center'>
                        <Button
                          onClick={() => {
                            if (data.identities) {
                              this.props.selectMembersOnPage(
                                identities.map(({ node: identity }) => identity.id)
                              )
                              this.toggleSelectModal()
                            }
                          }}>
                          Select records on page
                        </Button>
                        <Button
                          disabled={loading || error}
                          onClick={() => {
                            if (data.identities) {
                              this.props.selectAllMembers(
                                data.identities.edges.map(({ node: identity }) => identity.id)
                              )
                              this.toggleSelectModal()
                            }
                          }}>
                          {`Select all ${this.props.identities.totalCount}`}
                          {loading && <Spinner type='brand' size='x-small' />}
                        </Button>
                      </div>
                    </div>
                  }
                  contentClassName='modal-box-shadow-larger'
                  buttonIconInverse={false}
                  onCloseClick={this.toggleSelectModal}
                />
              )
            }
            else {
              return null
            }
          }}
        </Query>
      </div>
    )
  }
}

const getMemberIdentities = gql`
  query($audienceId: UUID!, $divisionId: UUID, $employmentType: String, $employmentStatus: String, $search: String, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime, $orderBy: String, $cursor: String, $recordsPerPage: Int) {
    identities(bulkmessageaudiences_Id: $audienceId, client_Division_Id: $divisionId, client_EmploymentType: $employmentType, client_Status: $employmentStatus, search: $search, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate, orderBy: $orderBy, first: $recordsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          firstName
          fullName
          identityValue
          msisdnDefault
          client {
            id
            employeeNumber
            employmentType
            status
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousMemberIdentities = gql`
  query($audienceId: UUID!, $divisionId: UUID, $employmentType: String, $employmentStatus: String, $search: String, $createdAtStartDate: DateTime, $createdAtEndDate: DateTime, $orderBy: String, $cursor: String, $recordsPerPage: Int) {
    identities(bulkmessageaudiences_Id: $audienceId, client_Division_Id: $divisionId, client_EmploymentType: $employmentType, client_Status: $employmentStatus, search: $search, createdAt_Gte: $createdAtStartDate, createdAt_Lte: $createdAtEndDate, orderBy: $orderBy, last: $recordsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          fullName
          identityValue
          msisdnDefault
          client {
            id
            employeeNumber
            employmentType
            status
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData =
  graphql(getMemberIdentities, {
    options: ({ match, search, audienceId, division, employmentType, employmentStatus, createdAtStartDate, createdAtEndDate, recordsPerPage, orderBy, cursor }) => ({
      variables: {
        search,
        audienceId: decodeBase64(audienceId),
        divisionId: division
          ? decodeBase64(division)
          : undefined,
        employmentType,
        employmentStatus,
        createdAtStartDate,
        createdAtEndDate,
        orderBy: orderBy || 'last_name',
        recordsPerPage: recordsPerPage || 10,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { match, search, audienceId, division, employmentType, employmentStatus, createdAtStartDate, createdAtEndDate, recordsPerPage, orderBy }, data: { loading, error, refetch, identities, fetchMore } }) => ({
      loading,
      error,
      refetch,
      identities,
      fetchNext: () => {
        return fetchMore({
          query: getMemberIdentities,
          variables: {
            search,
            audienceId: decodeBase64(audienceId),
            divisionId: division
              ? decodeBase64(division)
              : undefined,
            employmentType,
            employmentStatus,
            createdAtStartDate,
            createdAtEndDate,
            orderBy: orderBy || 'last_name',
            recordsPerPage: recordsPerPage || 10,
            cursor: identities.pageInfo.endCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.identities.edges
            const pageInfo = fetchMoreResult.identities.pageInfo
            return {
              identities: {
                __typename: previousResult.identities.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      fetchPrevious: () => {
        return fetchMore({
          query: getPreviousMemberIdentities,
          variables: {
            search,
            audienceId: decodeBase64(audienceId),
            divisionId: division
              ? decodeBase64(division)
              : undefined,
            employmentType,
            employmentStatus,
            createdAtStartDate,
            createdAtEndDate,
            orderBy: orderBy || 'last_name',
            recordsPerPage: recordsPerPage || 10,
            cursor: identities.pageInfo.startCursor
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.identities.edges
            const pageInfo = fetchMoreResult.identities.pageInfo
            return {
              identities: {
                __typename: previousResult.identities.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withRouter,
  withData
)(BulkMessageAudienceMembers)



// WEBPACK FOOTER //
// ./src/components/messageplus/bulk-message-audience-members/index.js